import React, { ComponentType, useCallback, useMemo } from "react";

import { Dayjs } from "dayjs";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import GeographicUserInsightsJs from "../../../shared/Dashboard/GeographicUserInsights/GeographicUserInsights";
import Select from "../../../shared/Select/Select";
import {
  USER_ANALYTICS_BY_STATE_ZERO_METRICS,
  type UserAnalyticsByStateMetric,
  type UserAnalyticsByStateResult,
} from "../../../../queries/user-analytics/userAnalyticsByStateTypes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getStatesByCountry } from "../../../../utils/MapStateName";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import type { AcquisitionSource } from "../../../../queries/acquisition/acquisitionSourceFilter";
import AcquisitionSourceSelect from "../AcquisitionSourceSelect";

import styles from "./AcquisitionByState.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;
const GeographicUserInsights = GeographicUserInsightsJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const METRIC_OPTIONS: ReadonlyArray<{
  value: UserAnalyticsByStateMetric;
  label: string;
}> = [
  { value: "new_users", label: "New Users" },
  { value: "ftd_count", label: "FTD Count" },
  { value: "ftd_amount", label: "FTD Amount" },
  { value: "avg_ftd_amount", label: "Avg FTD Amount" },
  { value: "total_users", label: "Total Users" },
];

// Only `ftd_amount` and `avg_ftd_amount` are dollar values. The other three
// are user counts that should be formatted as plain decimals by the map.
const CURRENCY_METRICS: ReadonlySet<UserAnalyticsByStateMetric> = new Set<
  UserAnalyticsByStateMetric
>(["ftd_amount", "avg_ftd_amount"]);

/**
 * Resolve the abbr → name list for the active country from the
 * `@svg-maps`-backed records in `MapStateName`. Falls back to the US
 * list — matches the legacy V1 behaviour and avoids a blank map when
 * the TCM config slice hasn't loaded yet.
 */
function buildStateList(
  country: string | undefined,
): ReadonlyArray<{ name: string; abbr: string }> {
  // `getStatesByCountry` is a JS module; cast to the concrete record
  // shape it returns so TS treats `name` as a string.
  const states = getStatesByCountry(country ?? "USA") as Record<string, string>;
  return Object.entries(states).map(([abbr, name]) => ({ abbr, name }));
}

export interface AcquisitionByStateProps {
  dates: DateRange;
  onDatesChange: (next: DateRange) => void;
  data: UserAnalyticsByStateResult | undefined;
  isFetching: boolean;
  isError: boolean;
  selectedMetric: UserAnalyticsByStateMetric;
  onMetricChange: (next: UserAnalyticsByStateMetric) => void;
  sources: AcquisitionSource[];
  onSourcesChange: (next: AcquisitionSource[]) => void;
  /** Picker preset highlight on first render (e.g. "Current Month"). */
  startRange?: string;
  /**
   * TCM-driven country code (`USA`, `MEXICO`, `NIGERIA`, …) used to
   * pick both the geo map SVG and the abbr→name list driving the
   * densified rows. Falls back to USA when undefined.
   */
  country?: string;
}

const AcquisitionByState: React.FC<AcquisitionByStateProps> = ({
  dates,
  onDatesChange,
  data,
  isFetching,
  isError,
  selectedMetric,
  onMetricChange,
  sources,
  onSourcesChange,
  startRange,
  country = "USA",
}) => {
  // Pick the abbr-set for the client's country (US for ReBet/DoggHouse,
  // MX for Infinito/Estadio Gana, NG for BetCorrect/Scorama). Memoised
  // because `Object.entries` allocates on every call.
  const stateList = useMemo(() => buildStateList(country), [country]);

  // Densify to every state in the active country list. The backend only
  // emits states with data (the map paints `amount === 0` as "Unavailable").
  const mapData = useMemo(
    () =>
      stateList.map((s) => {
        const metrics =
          data?.byAbbr[s.abbr] ?? USER_ANALYTICS_BY_STATE_ZERO_METRICS;
        return {
          name: s.name,
          abbr: s.abbr,
          amount: metrics[selectedMetric],
        };
      }),
    [data, selectedMetric, stateList],
  );

  // Legacy DatePicker contract — the picker still calls back through
  // `getData`, but the real fetch is driven by `dates` -> hook -> React Query.
  const getData = useCallback((_queryString: string | null) => undefined, []);

  const selectedLabel = useMemo(
    () =>
      METRIC_OPTIONS.find((opt) => opt.value === selectedMetric)?.label ??
      "Acquisition by State",
    [selectedMetric],
  );

  const isDollars = CURRENCY_METRICS.has(selectedMetric);
  const currencySymbol = useCurrencySymbol() ?? "$";

  const exportRows = useMemo(
    () =>
      stateList.map((s) => {
        const metrics =
          data?.byAbbr[s.abbr] ?? USER_ANALYTICS_BY_STATE_ZERO_METRICS;
        return {
          state: s.abbr,
          new_users: metrics.new_users,
          ftd_count: metrics.ftd_count,
          ftd_amount: metrics.ftd_amount,
          avg_ftd_amount: metrics.avg_ftd_amount,
          total_users: metrics.total_users,
        };
      }),
    [data, stateList],
  );

  return (
    <section className={styles.card} aria-label={`${selectedLabel} by State`}>
      <header className={styles.header}>
        <div className={styles.filterControls}>
          <Select
            className={styles.metricSelect}
            value={selectedMetric}
            options={METRIC_OPTIONS as any}
            onChange={(value) =>
              onMetricChange(value as UserAnalyticsByStateMetric)
            }
            aria-label="Metric"
          />
          <AcquisitionSourceSelect
            value={sources}
            onChange={onSourcesChange}
            loading={isFetching}
            className={styles.sourceSelect}
            testId="acquisition-by-state-source-select"
          />
        </div>
        <div className={styles.controls}>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={onDatesChange}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="acquisition-by-state.xlsx"
          />
        </div>
      </header>

      {isError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="acquisition-by-state-error"
        >
          {AppStrings.ACQUISITION_BY_STATE_LOAD_ERROR}
        </p>
      ) : null}

      <div className={styles.mapContainer}>
        {isFetching ? (
          <div className={styles.loaderWrapper}>
            <RebetLoader width={100} height={100} />
          </div>
        ) : (
          <GeographicUserInsights
            mapId="acquisition-by-state"
            data={mapData}
            isDollars={isDollars}
            country={country}
            currencySymbol={currencySymbol}
          />
        )}
      </div>
    </section>
  );
};

export default AcquisitionByState;
